import { useCallback, useMemo, useReducer } from 'react';
import type { PageItem } from './types';

/**
 * Undo and redo for the working canvas.
 *
 * The canvas is the page plan and the split markers — everything an arrangement
 * consists of. Deliberately NOT the documents themselves: removing a file
 * deletes its bytes from IndexedDB, so an undo that appeared to bring it back
 * would be lying. Those actions clear the history instead.
 *
 * Written as a reducer so every transition is pure. Pushing onto a history
 * stack from inside a setState updater would double-record under StrictMode,
 * which double-invokes updaters precisely to catch that kind of side effect.
 */

export interface CanvasState {
  pages: PageItem[];
  splitAfter: Set<string>;
}

/** Enough to undo a bad afternoon; bounded so a long session cannot grow forever. */
const LIMIT = 100;

type Updater = (current: CanvasState) => CanvasState;

type Action =
  | { type: 'apply'; updater: Updater; record: boolean }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'reset'; next: CanvasState };

interface History {
  past: CanvasState[];
  present: CanvasState;
  future: CanvasState[];
}

function reducer(history: History, action: Action): History {
  switch (action.type) {
    case 'apply': {
      const next = action.updater(history.present);
      // Nothing changed — a no-op must not consume an undo step.
      if (next === history.present) return history;
      if (!action.record) return { ...history, present: next };
      return {
        past: [...history.past, history.present].slice(-LIMIT),
        present: next,
        future: [],
      };
    }
    case 'undo': {
      const previous = history.past.at(-1);
      if (!previous) return history;
      return {
        past: history.past.slice(0, -1),
        present: previous,
        future: [history.present, ...history.future],
      };
    }
    case 'redo': {
      const [next, ...rest] = history.future;
      if (!next) return history;
      return { past: [...history.past, history.present], present: next, future: rest };
    }
    case 'reset':
      return { past: [], present: action.next, future: [] };
    default:
      return history;
  }
}

export function useCanvasHistory(initial: CanvasState) {
  const [history, dispatch] = useReducer(reducer, {
    past: [],
    present: initial,
    future: [],
  });

  /** Changes the canvas and records a step that can be undone. */
  const update = useCallback(
    (updater: Updater) => dispatch({ type: 'apply', updater, record: true }),
    [],
  );

  /**
   * Changes the canvas without recording — for things that are not an edit,
   * such as restoring documents from storage on load.
   */
  const replace = useCallback(
    (updater: Updater) => dispatch({ type: 'apply', updater, record: false }),
    [],
  );

  /** Starts again from this state; nothing before it can be undone. */
  const reset = useCallback((next: CanvasState) => dispatch({ type: 'reset', next }), []);

  const undo = useCallback(() => dispatch({ type: 'undo' }), []);
  const redo = useCallback(() => dispatch({ type: 'redo' }), []);

  return useMemo(
    () => ({
      pages: history.present.pages,
      splitAfter: history.present.splitAfter,
      canUndo: history.past.length > 0,
      canRedo: history.future.length > 0,
      update,
      replace,
      reset,
      undo,
      redo,
    }),
    [history, update, replace, reset, undo, redo],
  );
}
